import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import.meta.env.VITE_API_URL;

const VehicleBasicInfo = ({ 
  formData, 
  onFormChange, 
  errors = {}, 
  onVinLookup 
}) => {
  const apiUrl = import.meta.env.VITE_API_URL;

  const currentYear = new Date().getFullYear();
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  
  // Effect to fetch the list of all manufacturers once on mount
  useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/manufacturers`);
        const data = await response.json();
        setManufacturers(data.map(m => ({ value: m.id, label: m.name })));
      } catch (err) {
        console.error('Failed to fetch manufacturers:', err);
      }
    };
    fetchManufacturers();
  }, []);

  // Effect to fetch models whenever the 'make' in formData changes
  useEffect(() => {
    const fetchModels = async () => {
      if (formData.make) {
        try {
          const response = await fetch(`${apiUrl}/api/manufacturers/${formData.make}/models`);
          const data = await response.json();
          setModels(data.models.map(m => ({ value: m.id, label: m.name })));
        } catch (err) {
          console.error('Failed to fetch models:', err);
        }
      }
    };
    fetchModels();
  }, [formData.make]); // This is the key dependency

  const handleMakeChange = (value) => {
    // Update the parent state for 'make' and reset 'model'
    onFormChange('make', value);
    onFormChange('model', '');
  };

  const yearOptions = [
    { value: '', label: 'Select Year' },
    ...Array.from({ length: 30 }, (_, i) => {
      const year = currentYear - i;
      return { value: year.toString(), label: year.toString() };
    })
  ];

  const colorOptions = [
    { value: '', label: 'Select Color' },
    { value: 'black', label: 'Black' },
    { value: 'white', label: 'White' },
    { value: 'silver', label: 'Silver' },
    { value: 'gray', label: 'Gray' },
    { value: 'red', label: 'Red' },
    { value: 'blue', label: 'Blue' },
    { value: 'green', label: 'Green' },
    { value: 'yellow', label: 'Yellow' },
    { value: 'orange', label: 'Orange' },
    { value: 'brown', label: 'Brown' },
    { value: 'purple', label: 'Purple' },
    { value: 'pink', label: 'Pink' },
    { value: 'gold', label: 'Gold' },
    { value: 'bronze', label: 'Bronze' },
    { value: 'copper', label: 'Copper' },
    { value: 'turquoise', label: 'Turquoise' },
    { value: 'cyan', label: 'Cyan' },
    { value: 'magenta', label: 'Magenta' },
  ];


  const transmissionOptions = [
    { value: '', label: 'Select Transmission' },
    { value: 'automatic', label: 'Automatic' },
    { value: 'manual', label: 'Manual' },
    { value: 'semiauto', label: 'Semi Automatic' }
  ];

  const fuelTypeOptions = [
    { value: '', label: 'Select Fuel Type' },
    { value: 'gasoline', label: 'Gasoline' },
    { value: 'diesel', label: 'Diesel' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'electric', label: 'Electric' }
  ];

  const handleVinChange = (e) => {
    const vin = e.target.value.toUpperCase();
    onFormChange('vin', vin);
    if (vin.length === 17) {
      onVinLookup(vin);
    }
  };

  const getAvailableModels = () => {
    if (!formData.make) return [{ value: '', label: 'Select Make First' }];
    return models;
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Basic Vehicle Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Input
              label="VIN Number"
              type="text"
              placeholder="Enter 17-character VIN"
              value={formData.vin || ''}
              onChange={handleVinChange}
              error={errors.vin}
              description="VIN will auto-populate vehicle details"
              maxLength={17}
              className="font-mono"
            />
          </div>

          <Select
            label="Make"
            options={manufacturers}
            value={formData.make || ''}
            onChange={handleMakeChange} // Use the new handler
            error={errors.make}
            required
            searchable
          />

          <Select
            label="Model"
            options={getAvailableModels()}
            value={formData.model || ''}
            onChange={(value) => onFormChange('model', value)}
            error={errors.model}
            required
            disabled={!formData.make}
            searchable
          />

          <Select
            label="Year"
            options={yearOptions}
            value={formData.year || ''}
            onChange={(value) => onFormChange('year', value)}
            error={errors.year}
            required
            searchable
          />

          <Input
            label="Mileage"
            type="number"
            placeholder="Enter current mileage"
            value={formData.mileage || ''}
            onChange={(e) => onFormChange('mileage', e.target.value)}
            error={errors.mileage}
            description="Current odometer reading"
            required
          />

          <Select
            label="Transmission"
            options={transmissionOptions}
            value={formData.transmission || ''}
            onChange={(value) => onFormChange('transmission', value)}
            error={errors.transmission}
            required
          />

          <Select
            label="Fuel Type"
            options={fuelTypeOptions}
            value={formData.fuelType || ''}
            onChange={(value) => onFormChange('fuelType', value)}
            error={errors.fuelType}
            required
          />

        <Input
            label="Cubic Capacity"
            type="number"
            placeholder="Enter current cubic capacity"
            value={formData.cubic_capacity || ''}
            onChange={(e) => onFormChange('cubic_capacity', e.target.value)}
            error={errors.cubic_capacity}
            required
          />

          <Select
            label="Color"
            options={colorOptions}
            value={formData.color || ''}
            onChange={(value) => onFormChange('color', value)}
            error={errors.color}
            required
          />

        </div>
      </div>

      {formData.vin && formData.vin.length === 17 && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 text-success">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">VIN validated - Vehicle details auto-populated</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleBasicInfo;